import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { lightFormat, startOfYear, subMonths } from 'date-fns';

import { AccountingApi } from '_api';
import { FIELD_KEYS } from '_constants/forms';
import { FILTER_TYPES, MERGE_TYPES, REPORT_KEYS } from '_constants/reports';
import { TRANSACTION_TYPES } from '_constants/transactions';
import { handleError, handleResponse } from '_helpers/api';
import { createParamsPayload } from '_helpers/reports';

export const getExpensesGraph = createAsyncThunk(
  'reports/getExpensesGraph',
  async function ({ customerId, productId }, { getState, rejectWithValue }) {
    try {
      const { reports } = getState();
      const { params } = reports[REPORT_KEYS.expensesGraph] || {};
      const { from, to, mergeTo } = params || {};

      const response = await AccountingApi.getGraph({
        customerId,
        productId,
        from,
        to,
        mergeTo,
        type: TRANSACTION_TYPES.spend,
      });

      return handleResponse(response);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const getIncomeGraph = createAsyncThunk(
  'reports/getIncomeGraph',
  async function ({ customerId, productId }, { getState, rejectWithValue }) {
    try {
      const { reports } = getState();
      const { params } = reports[REPORT_KEYS.incomeGraph] || {};
      const { from, to, mergeTo } = params || {};

      const response = await AccountingApi.getGraph({
        customerId,
        productId,
        from,
        to,
        mergeTo,
        type: TRANSACTION_TYPES.receive,
      });

      return handleResponse(response);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const getInsightsGraph = createAsyncThunk(
  'reports/getInsightsGraph',
  async function ({ customerId, productId }, { getState, rejectWithValue }) {
    try {
      const { reports } = getState();
      const { params } = reports[REPORT_KEYS.insightsGraph] || {};
      const { from, to, mergeTo, type } = params || {};

      const response = await AccountingApi.getGraph({
        customerId,
        productId,
        from,
        to,
        mergeTo,
        type,
      });

      return handleResponse(response);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const getBankTransactions = createAsyncThunk(
  'reports/getBankTransactions',
  async function ({ customerId, productId }, { getState, rejectWithValue }) {
    try {
      const { reports } = getState();
      const { params } = reports[REPORT_KEYS.bankTransactions] || {};
      const { from, to, page, count, isReconcile } = params || {};

      const response = await AccountingApi.getBankTransactions({
        customerId,
        productId,
        from,
        to,
        page,
        count,
        isReconcile,
      });

      return handleResponse(response);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const updateFilters = createAsyncThunk('reports/updateFilters', async function (filtersPayload, { dispatch }) {
  dispatch(refreshFilters(filtersPayload));
  dispatch(refreshParams(createParamsPayload(filtersPayload)));
});

const initialState = {
  [REPORT_KEYS.expensesGraph]: {
    filters: {
      [FIELD_KEYS.filterType]: FILTER_TYPES.yearToDate,
      [FIELD_KEYS.dateRangeFrom]: startOfYear(new Date()),
      [FIELD_KEYS.dateRangeTo]: new Date(),
    },
    params: {
      from: lightFormat(startOfYear(new Date()), 'yyyy-MM-dd'),
      to: lightFormat(new Date(), 'yyyy-MM-dd'),
      mergeTo: MERGE_TYPES.month,
    },
    data: {},
    error: null,
    loading: false,
  },
  [REPORT_KEYS.incomeGraph]: {
    filters: {
      [FIELD_KEYS.filterType]: FILTER_TYPES.yearToDate,
      [FIELD_KEYS.dateRangeFrom]: startOfYear(new Date()),
      [FIELD_KEYS.dateRangeTo]: new Date(),
    },
    params: {
      from: lightFormat(startOfYear(new Date()), 'yyyy-MM-dd'),
      to: lightFormat(new Date(), 'yyyy-MM-dd'),
      mergeTo: MERGE_TYPES.month,
    },
    data: {},
    error: null,
    loading: false,
  },
  [REPORT_KEYS.insightsGraph]: {
    filters: {
      [FIELD_KEYS.filterType]: FILTER_TYPES.yearToDate,
      [FIELD_KEYS.dateRangeFrom]: startOfYear(new Date()),
      [FIELD_KEYS.dateRangeTo]: new Date(),
      [FIELD_KEYS.type]: TRANSACTION_TYPES.spend,
    },
    params: {
      from: lightFormat(startOfYear(new Date()), 'yyyy-MM-dd'),
      to: lightFormat(new Date(), 'yyyy-MM-dd'),
      mergeTo: MERGE_TYPES.month,
      type: TRANSACTION_TYPES.spend,
    },
    data: {},
    error: null,
    loading: false,
  },
  [REPORT_KEYS.bankTransactions]: {
    filters: {
      [FIELD_KEYS.from]: subMonths(new Date(), 1),
      [FIELD_KEYS.to]: new Date(),
      [FIELD_KEYS.page]: 1,
      [FIELD_KEYS.count]: 25,
      [FIELD_KEYS.reconcile]: '',
    },
    params: {
      from: lightFormat(subMonths(new Date(), 1), 'yyyy-MM-dd'),
      to: lightFormat(new Date(), 'yyyy-MM-dd'),
      page: 1,
      count: 25,
      isReconcile: null,
    },
    data: {},
    error: null,
    loading: false,
  },
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    refreshFilters(state, action) {
      const keys = Object.keys(action.payload);

      keys.forEach((key) => {
        state[key].filters = {
          ...state[key].filters,
          ...action.payload[key],
        };
      });
    },
    refreshParams(state, action) {
      const keys = Object.keys(action.payload);

      keys.forEach((key) => {
        state[key].params = {
          ...state[key].params,
          ...action.payload[key],
        };
      });
    },
    resetDataByKeys(state, action) {
      action.payload?.forEach((key) => {
        state[key].data = {};
      });
    },
    resetErrors(state) {
      state.expenses.error = null;
    },
    resetAll(state) {
      const keys = Object.keys(initialState);

      keys.forEach((key) => {
        state[key] = {
          ...state[key],
          ...initialState[key],
        };
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExpensesGraph.pending, (state) => {
        state[REPORT_KEYS.expensesGraph].data = {};
        state[REPORT_KEYS.expensesGraph].error = null;
        state[REPORT_KEYS.expensesGraph].loading = true;
      })
      .addCase(getExpensesGraph.fulfilled, (state, action) => {
        state[REPORT_KEYS.expensesGraph].data = action.payload;
        state[REPORT_KEYS.expensesGraph].loading = false;
      })
      .addCase(getExpensesGraph.rejected, (state, action) => {
        state[REPORT_KEYS.expensesGraph].error = action.payload;
        state[REPORT_KEYS.expensesGraph].loading = false;
      });

    builder
      .addCase(getIncomeGraph.pending, (state) => {
        state[REPORT_KEYS.incomeGraph].data = {};
        state[REPORT_KEYS.incomeGraph].error = null;
        state[REPORT_KEYS.incomeGraph].loading = true;
      })
      .addCase(getIncomeGraph.fulfilled, (state, action) => {
        state[REPORT_KEYS.incomeGraph].data = action.payload;
        state[REPORT_KEYS.incomeGraph].loading = false;
      })
      .addCase(getIncomeGraph.rejected, (state, action) => {
        state[REPORT_KEYS.incomeGraph].error = action.payload;
        state[REPORT_KEYS.incomeGraph].loading = false;
      });

    builder
      .addCase(getInsightsGraph.pending, (state) => {
        state[REPORT_KEYS.insightsGraph].data = {};
        state[REPORT_KEYS.insightsGraph].error = null;
        state[REPORT_KEYS.insightsGraph].loading = true;
      })
      .addCase(getInsightsGraph.fulfilled, (state, action) => {
        state[REPORT_KEYS.insightsGraph].data = action.payload;
        state[REPORT_KEYS.insightsGraph].loading = false;
      })
      .addCase(getInsightsGraph.rejected, (state, action) => {
        state[REPORT_KEYS.insightsGraph].error = action.payload;
        state[REPORT_KEYS.insightsGraph].loading = false;
      });

    builder
      .addCase(getBankTransactions.pending, (state) => {
        state[REPORT_KEYS.bankTransactions].error = null;
        state[REPORT_KEYS.bankTransactions].loading = true;
      })
      .addCase(getBankTransactions.fulfilled, (state, action) => {
        state[REPORT_KEYS.bankTransactions].data = action.payload;
        state[REPORT_KEYS.bankTransactions].loading = false;
      })
      .addCase(getBankTransactions.rejected, (state, action) => {
        state[REPORT_KEYS.bankTransactions].data = {};
        state[REPORT_KEYS.bankTransactions].error = action.payload;
        state[REPORT_KEYS.bankTransactions].loading = false;
      });
  },
});

export const { refreshFilters, refreshParams, resetDataByKeys, resetErrors, resetAll } = reportsSlice.actions;

export default reportsSlice.reducer;
